import {useInfiniteQuery} from '@tanstack/react-query'
import {useIntersectionObserver} from 'usehooks-ts'
import {HiMiniArrowRight} from 'react-icons/hi2'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {useTranslations} from 'use-intl'
import {DesignCollectionItem} from '~/client/spreadshop/missing-types'
import {productsInfiniteQueryOptions} from '~/client/fn/products'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'

type PerfectFitProductsProps = {
    design: DesignCollectionItem
    preload: boolean
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
    '(min-width: 1024px)': {
        slidesToShow: 4.25,
        slidesToScroll: 4,
    },
}

const PerfectFitProducts = ({design, preload}: PerfectFitProductsProps) => {
    const params = useLocaleParams()
    const t = useTranslations('perfectFit.results')
    const {isIntersecting, ref} = useIntersectionObserver({
        rootMargin: '10%',
        initialIsIntersecting: preload,
    })
    const {data: products, isLoading} = useInfiniteQuery({
        ...productsInfiniteQueryOptions(
            undefined,
            undefined,
            undefined,
            undefined,
            design.id,
            undefined,
            params.locale
        ),
        enabled: isIntersecting || preload,
    })

    return (
        <div ref={ref}>
            <div className="flex flex-row items-center justify-between mb-2">
                <h2 className="text-lg font-bold">{design.name}</h2>
                <Link
                    to="/$lang/products"
                    params={params}
                    search={{design: design.id}}
                    className="flex flex-row items-center gap-1 font-medium text-green-600 text-sm md:text-base"
                >
                    <span>{t('showAll')}</span>
                    <HiMiniArrowRight className="size-4" />
                </Link>
            </div>
            <div className="relative">
                <Carousel
                    config={config}
                    showDots={false}
                    showDisabledArrows={true}
                    initialSlidesToShow={4.25}
                >
                    <Card>
                        <ImageComposition
                            imageClassName="p-4 rounded"
                            image={design.image!}
                            sizes="500px"
                        />
                        <div className="p-4 text-sm font-medium text-center truncate">
                            {t('designLabel', {design: design.name})}
                        </div>
                    </Card>
                    {isLoading &&
                        [...new Array(3)].map((_, index) => (
                            <Card key={index} className="p-4">
                                <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                                <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                            </Card>
                        ))}

                    {products?.map((product) => (
                        <Link
                            className="block h-full group outline-none"
                            key={product.id}
                            to="/$lang/products/$productId"
                            params={{...params, productId: product.id!}}
                        >
                            <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-purple-600 transition duration-200 ease-in-out transform-gpu">
                                <ImageComposition
                                    className="overflow-hidden"
                                    imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                    image={product.image!}
                                    sizes="500px"
                                />
                                <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                    <ImageComposition
                                        className="rounded-md overflow-hidden bg-white"
                                        image={product?.designImage!}
                                        lazy={true}
                                        sizes="500px"
                                    />
                                </div>
                                <div className="flex gap-4 flex-row justify-between p-4 text-sm font-medium">
                                    <div className="truncate">{product.type?.name}</div>
                                    <Price className="text-nowrap">
                                        {product.price!.vatIncluded}
                                    </Price>
                                </div>
                            </Card>
                        </Link>
                    ))}
                </Carousel>
            </div>
        </div>
    )
}

export default PerfectFitProducts
